import React from 'react';

export type MultiSelectOption = {
  label: string;
  value: string;
};

type MultiSelectMessageProps = {
  options: MultiSelectOption[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  disabled?: boolean;
  maxSelect?: number;
  validateErrorMessage?: string | null;
  showError?: boolean;
  ariaLabel?: string;
  // Localized min/max constraint sentence, announced as the group description
  constraintText?: string | null;
  // Localized announcement when maxSelect disables the remaining options
  maxReachedText?: string;
  // Space-separated ids of external elements describing the group (prompt text)
  describedBy?: string;
};

/**
 * MultiSelectMessage component
 * Renders the checkbox list for multi-select chat interactions. Sending is
 * handled by the general send button. The prompt label above the checkboxes
 * is rendered by the parent ChatWindow so alignment stays consistent.
 */
export const MultiSelectMessage: React.FC<MultiSelectMessageProps> = ({
  options,
  selected,
  onToggle,
  disabled,
  maxSelect,
  validateErrorMessage,
  showError,
  ariaLabel,
  constraintText,
  maxReachedText,
  describedBy,
}) => {
  const effectiveMax = maxSelect != null && maxSelect > 0 ? maxSelect : 0;
  const maxReached = effectiveMax > 0 && selected.size >= effectiveMax;

  const constraintId = 'omq-chatbot-multi-select-constraint';
  const errorId = 'omq-chatbot-multi-select-error';
  const groupDescribedBy =
    [
      describedBy,
      constraintText ? constraintId : null,
      showError && validateErrorMessage ? errorId : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div
      className="omq-chatbot-multi-select"
      role="group"
      aria-label={ariaLabel || 'Select options'}
      aria-describedby={groupDescribedBy}>
      {constraintText && (
        <span id={constraintId} className="omq-sr-only">
          {constraintText}
        </span>
      )}
      {options.map((opt) => (
        <label key={opt.value} className="omq-chatbot-multi-select-option">
          <input
            type="checkbox"
            checked={selected.has(opt.value)}
            onChange={() => onToggle(opt.value)}
            disabled={disabled || (!selected.has(opt.value) && maxReached)}
          />
          <span>{opt.label}</span>
        </label>
      ))}
      <span className="omq-sr-only" role="status" aria-live="polite">
        {maxReached ? maxReachedText || 'Maximum number of selections reached.' : ''}
      </span>
      {showError && validateErrorMessage && (
        <div id={errorId} className="omq-chatbot-multi-select-error" role="alert">
          {validateErrorMessage}
        </div>
      )}
    </div>
  );
};
